// TODO(ts-migration): large dialog entangled with untyped .js services/components
import {
  Box, Button, Dialog, DialogContent, IconButton, InputAdornment, MenuItem, TextField, Typography
} from "@mui/material";
import {
  Close as CloseIcon,
  ManageAccounts as ProfileIcon
} from "@mui/icons-material";
import {useMemo} from "react";
import SelectCom from "../SelectCom";
import {DateField} from "@mui/x-date-pickers";
import {ReactComponent as GiftIcon} from "../../assets/images/gift.svg";
import dayjs from "dayjs";
import * as Yup from "yup";
import {useFormik} from "formik";
import {updateTrainee} from "../../services/TraineeService";
import moment from "moment";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {EquipmentTypes} from "../../enums/EquipmentType";
import {experienceLevels} from "../../enums/ExperienceLevels";
import PhoneInput from "../shared/PhoneInput";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: borders.radius.md,
    backgroundColor: colors.white,
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary, borderWidth: "2px" }
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border }
};

const labelSx = { fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px" };
const errorSx = { color: colors.error, fontSize: "12px", marginTop: "4px" };

const EditClientDialog = ({navigate, trainee, close, isOpen, reload}) => {
  const {notificationHandler} = useMessageContext();

  const schema = Yup.object().shape({
    name: Yup.string().required('שדה חובה'),
    phoneNumber: Yup.string().required('שדה חובה'),
    email: Yup.string().email("אימייל לא חוקי").required('שדה חובה'),
    dateOfBirth: Yup.string().nullable(),
  });

  // The dialog is mounted for the lifetime of the action menu (only `isOpen` toggles),
  // so formik would keep the values captured on the FIRST mount forever. Re-derive them
  // whenever the parent hands us a refetched trainee, and let formik reinitialize —
  // otherwise saving and reopening shows the pre-save values until a page refresh.
  const initialValues = useMemo(() => ({
    name: trainee ? trainee.name : "",
    phoneNumber: trainee ? trainee.phoneNumber : "",
    email: trainee ? trainee.email : "",
    gender: trainee ? trainee.gender : "null",
    experienceLevel: trainee ? trainee.experienceLevel : "null",
    equipmentType: trainee ? trainee.equipmentType : "null",
    numOfDaysTraining: trainee ? trainee.numOfDaysTraining : 0,
    height: trainee ? trainee.height : "",
    id: trainee?.id,
    expirationDate: trainee?.expirationDate ? dayjs(trainee.expirationDate * 1000) : null,
    dateOfBirth: trainee?.dateOfBirth ? dayjs(trainee.dateOfBirth * 1000) : null,
  }), [trainee]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values, {resetForm}) => {
      let body = {...values};
      body.gender = body.gender === "null" ? null : body.gender;
      body.experienceLevel = body.experienceLevel === "null" ? null : body.experienceLevel;
      body.equipmentType = body.equipmentType === "null" ? null : body.equipmentType;
      body.dateOfBirth = body.dateOfBirth ? moment(body.dateOfBirth.$d).unix() : null;
      body.expirationDate = body.expirationDate ? moment(body.expirationDate.$d).unix() : null;
      body.height = body.height !== "" && body.height !== null ? parseFloat(body.height) : null;

      updateTrainee(body)
          .then(() => reload())
          .then(handleClose)
          .then(() => resetForm())
          .then(() => notificationHandler.success("מתאמן עודכן בהצלחה"))
          .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
    },
    validationSchema: schema,
  });

  const handleClose = () => {
    // Reset back to the trainee's current values (passing `{values: ''}` blew the whole
    // values object away and left the inputs uncontrolled on the next open).
    formik.resetForm();
    close();
  };

  return (
      <Dialog
          open={isOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: borders.radius.lg,
              minWidth: {xs: "95%", md: "600px"},
              maxWidth: {xs: "95%", md: "720px"},
              maxHeight: {xs: "90vh", md: "85vh"},
              overflowY: "auto"
            }
          }}
      >
        <RTL>
          <Box dir="rtl">
            {/* Header */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              backgroundColor: colors.bg,
              borderBottom: borders.default
            }}>
              <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <ProfileIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />
                <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.textPrimary }}>
                  פרטים אישיים
                </Typography>
              </Box>
              <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{ color: colors.textMuted, "&:hover": { backgroundColor: colors.bgHover, color: colors.textPrimary } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Content */}
            <form onSubmit={formik.handleSubmit}>
              <DialogContent sx={{padding: "24px 20px"}}>
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                  gap: "16px",
                }}>
                  {/* Name */}
                  <Box>
                    <Typography sx={labelSx}>*שם מלא</Typography>
                    <TextField sx={inputSx} fullWidth size="small"
                        onBlur={formik.handleBlur("name")} value={formik.values.name}
                        onChange={formik.handleChange("name")} id="name" variant="outlined" />
                    {formik.errors.name && formik.touched.name && <Typography sx={errorSx}>{formik.errors.name}</Typography>}
                  </Box>

                  {/* Email */}
                  <Box>
                    <Typography sx={labelSx}>*מייל</Typography>
                    <TextField sx={{...inputSx, direction: "ltr"}} fullWidth size="small"
                        onBlur={formik.handleBlur} value={formik.values.email}
                        onChange={formik.handleChange} id="email" variant="outlined" />
                    {formik.errors.email && formik.touched.email && <Typography sx={errorSx}>{formik.errors.email}</Typography>}
                  </Box>

                  {/* Phone */}
                  <Box>
                    <Typography sx={labelSx}>טלפון</Typography>
                    <PhoneInput sx={inputSx} value={formik.values.phoneNumber}
                        onChange={(value) => formik.setFieldValue("phoneNumber", value)} />
                    {formik.errors.phoneNumber && formik.touched.phoneNumber && <Typography sx={errorSx}>{formik.errors.phoneNumber}</Typography>}
                  </Box>

                  {/* Date of Birth */}
                  <Box>
                    <Typography sx={labelSx}>תאריך לידה</Typography>
                    <DateField fullWidth size="small" sx={inputSx}
                        InputProps={{
                          endAdornment: (<InputAdornment position="end">
                            <IconButton disabled edge="start"><GiftIcon/></IconButton>
                          </InputAdornment>),
                        }}
                        onChange={(value) => formik.setFieldValue('dateOfBirth', dayjs(value))}
                        value={formik.values.dateOfBirth} format={"DD/MM/YYYY"} />
                  </Box>

                  {/* Gender */}
                  <Box>
                    <SelectCom id={"gender"} width={"100%"} height={"40px"} mainItem={"לא נבחר"}
                        label={"מגדר"} contrast={"black"} onBlur={formik.handleBlur("gender")}
                        value={formik.values.gender} onChangeReal={formik.handleChange("gender")} defaultValue={"null"}>
                      <MenuItem value={"MALE"} sx={{display: "flex", alignItems: "center"}}>
                        <Typography sx={{marginLeft: "8px"}}>זכר</Typography>
                      </MenuItem>
                      <MenuItem value={"FEMALE"} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <Typography sx={{marginLeft: "8px"}}>נקבה</Typography>
                      </MenuItem>
                    </SelectCom>
                  </Box>

                  {/* Expiration Date */}
                  <Box>
                    <Typography sx={labelSx}>*תאריך סיום</Typography>
                    <DateField fullWidth size="small" sx={inputSx}
                        onBlur={formik.handleBlur} value={formik.values.expirationDate}
                        onChange={(value) => formik.setFieldValue('expirationDate', dayjs(value))}
                        format={"DD/MM/YYYY"} />
                  </Box>

                  {/* Experience Level */}
                  <Box>
                    <SelectCom id={"experienceLevel"} height={"40px"} width={"100%"} mainItem={"לא נבחר"}
                        label={"רמת מתאמן"} contrast={"black"} onBlur={formik.handleBlur("experienceLevel")}
                        value={formik.values.experienceLevel} onChangeReal={formik.handleChange("experienceLevel")} defaultValue={"null"}>
                      {experienceLevels.map((res, i) => (
                          <MenuItem key={i} value={res.value} sx={{display: "flex", alignItems: "center"}}>
                            <Typography sx={{marginLeft: "8px"}}>{res.name}</Typography>
                          </MenuItem>
                      ))}
                    </SelectCom>
                  </Box>

                  {/* Equipment Type */}
                  <Box>
                    <SelectCom id={"equipmentType"} height={"40px"} width={"100%"} mainItem={"לא נבחר"}
                        label={"ציוד קיים"} contrast={"black"} onBlur={formik.handleBlur("equipmentType")}
                        value={formik.values.equipmentType} onChangeReal={formik.handleChange("equipmentType")} defaultValue={"null"}>
                      {EquipmentTypes.map((res, i) => (
                          <MenuItem key={i} value={res.value} sx={{display: "flex", alignItems: "center"}}>
                            <Typography sx={{marginLeft: "8px"}}>{res.name}</Typography>
                          </MenuItem>
                      ))}
                    </SelectCom>
                  </Box>

                  {/* Training days per week */}
                  <Box>
                    <Typography sx={labelSx}>מספר אימונים בשבוע</Typography>
                    <TextField sx={inputSx} fullWidth size="small" type={"number"}
                        onBlur={formik.handleBlur} value={formik.values.numOfDaysTraining}
                        onChange={formik.handleChange} id="numOfDaysTraining" variant="outlined" />
                  </Box>

                  {/* Height */}
                  <Box>
                    <Typography sx={labelSx}>גובה (ס״מ)</Typography>
                    <TextField sx={inputSx} fullWidth size="small" type={"number"}
                        onBlur={formik.handleBlur} value={formik.values.height}
                        onChange={formik.handleChange} id="height" variant="outlined" placeholder="לדוגמה: 175" />
                  </Box>
                </Box>
              </DialogContent>

              {/* Footer */}
              <Box sx={{
                display: "flex", justifyContent: "flex-start", gap: 1.5,
                padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg
              }}>
                <Button disabled={!formik.dirty || !formik.isValid} type={"submit"} variant="contained"
                    sx={{
                      backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600,
                      padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none",
                      "&:hover": { backgroundColor: colors.primaryDark },
                      "&.Mui-disabled": { backgroundColor: colors.border, color: colors.textMuted }
                    }}>
                  שמור
                </Button>
                <Button onClick={handleClose} variant="outlined"
                    sx={{
                      borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500,
                      padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none",
                      "&:hover": { borderColor: colors.textSecondary, backgroundColor: colors.bgHover }
                    }}>
                  ביטול
                </Button>
              </Box>
            </form>
          </Box>
        </RTL>
      </Dialog>
  );
};

export default EditClientDialog;
